import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
    Sun, BookOpen, Heart, PlayCircle, PenLine, 
    Sparkles, RefreshCw, Calendar, TrendingUp, Users, Brain 
} from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import MoodTracker from '../components/MoodTracker';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Dashboard() {
    const { user } = useAuth();
    const [inspiration, setInspiration] = useState(null);
    const [mindset, setMindset] = useState(null);
    const [todayMood, setTodayMood] = useState(null);
    const [stats, setStats] = useState({ total_journals: 0, total_moods: 0, streak: 0 });
    const [aiDevotional, setAiDevotional] = useState(null);
    const [loadingAi, setLoadingAi] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDailyData();
    }, []);

    const fetchDailyData = async () => {
        try {
            const [inspirationRes, mindsetRes, moodRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/inspiration/daily`),
                axios.get(`${API_URL}/inspiration/mindset`),
                axios.get(`${API_URL}/moods/today`),
                axios.get(`${API_URL}/stats`)
            ]);
            setInspiration(inspirationRes.data);
            setMindset(mindsetRes.data);
            setTodayMood(moodRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Failed to fetch daily data:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateAiDevotional = async () => {
        setLoadingAi(true);
        try {
            const response = await axios.post(`${API_URL}/inspiration/generate`);
            setAiDevotional(response.data.ai_devotional);
            toast.success('New devotional generated!');
        } catch (error) {
            toast.error('Failed to generate devotional. Please try again.');
        } finally {
            setLoadingAi(false);
        }
    };

    const handleMoodSelect = (mood) => {
        setTodayMood({ mood });
        fetchDailyData();
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
                <div className="loading-spinner" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFCF8]" data-testid="dashboard-page">
            <Navigation />
            
            <main className="container-app py-8 md:py-12">
                {/* Header */}
                <div className="mb-10 animate-fade-in">
                    <p className="text-sm text-primary font-medium tracking-wide uppercase mb-2">
                        {inspiration?.date}
                    </p>
                    <h1 className="font-display text-3xl md:text-4xl font-medium text-[#2D2A26]">
                        {getGreeting()}, {user?.name?.split(' ')[0]}
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        May the Lord bless and keep you today
                    </p>
                </div>

                {/* Bento Grid Layout */}
                <div className="dashboard-grid">
                    {/* Verse of the Day - Large Card */}
                    <div className="bento-large animate-fade-in" data-testid="verse-card">
                        <div className="verse-card h-full gradient-divine">
                            <div className="flex items-center gap-2 mb-6">
                                <Sun className="w-5 h-5 text-primary" />
                                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                                    Verse of the Day
                                </span>
                            </div>
                            <blockquote className="font-display text-xl md:text-2xl lg:text-3xl text-[#2D2A26] leading-relaxed mb-6">
                                "{inspiration?.verse}"
                            </blockquote>
                            <p className="font-accent text-lg text-primary">
                                — {inspiration?.verse_reference}
                            </p>
                        </div>
                    </div>

                    {/* Quick Stats - Sidebar */}
                    <div className="bento-sidebar space-y-4 animate-fade-in stagger-1">
                        <div className="glass rounded-2xl p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-semibold text-[#2D2A26]">{stats.streak}</p>
                                    <p className="text-sm text-muted-foreground">Day Streak</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="glass rounded-2xl p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                                    <PenLine className="w-5 h-5 text-accent" />
                                </div>
                                <div>
                                    <p className="text-2xl font-semibold text-[#2D2A26]">{stats.total_journals}</p>
                                    <p className="text-sm text-muted-foreground">Journal Entries</p>
                                </div>
                            </div>
                        </div>

                        <div className="glass rounded-2xl p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-secondary-foreground" />
                                </div>
                                <div>
                                    <p className="text-2xl font-semibold text-[#2D2A26]">{stats.total_moods}</p>
                                    <p className="text-sm text-muted-foreground">Mood Logs</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Daily Quote */}
                    <div className="bento-medium animate-fade-in stagger-2">
                        <div className="journal-card h-full">
                            <div className="flex items-center gap-2 mb-4">
                                <Sparkles className="w-5 h-5 text-primary" />
                                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                                    Daily Inspiration
                                </span>
                            </div>
                            <p className="font-accent text-lg md:text-xl text-[#2D2A26] leading-relaxed italic">
                                "{inspiration?.quote}"
                            </p>
                        </div>
                    </div>

                    {/* Daily Mindset Motivation - NEW SECTION */}
                    <div className="col-span-12 animate-fade-in stagger-2" data-testid="mindset-section">
                        <div className="verse-card gradient-sunrise">
                            <div className="flex items-center gap-2 mb-6">
                                <Brain className="w-5 h-5 text-primary" />
                                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                                    Daily Mindset Motivation
                                </span>
                            </div>
                            
                            {mindset && (
                                <div className="space-y-6">
                                    {/* Affirmation */}
                                    <div className="text-center">
                                        <p className="font-display text-2xl md:text-3xl text-[#2D2A26] font-medium">
                                            "{mindset.affirmation}"
                                        </p>
                                        <p className="text-sm text-primary mt-2">— {mindset.scripture} (KJV)</p>
                                    </div>
                                    
                                    {/* Motivation */}
                                    <div className="bg-white/50 rounded-2xl p-6">
                                        <p className="text-lg text-[#2D2A26] leading-relaxed text-center">
                                            {mindset.motivation}
                                        </p>
                                    </div>
                                    
                                    {/* Action prompt */}
                                    <div className="text-center">
                                        <p className="text-sm text-muted-foreground italic">
                                            Speak this affirmation out loud and carry it with you today.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mood Tracker */}
                    <div className="bento-medium animate-fade-in stagger-3">
                        <div className="glass-heavy rounded-3xl p-6 md:p-8 h-full">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-2">
                                    <Heart className="w-5 h-5 text-primary" />
                                    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                                        How are you feeling?
                                    </span>
                                </div>
                                {todayMood && (
                                    <span className="text-sm text-primary font-medium">
                                        Today: {todayMood.mood}
                                    </span>
                                )}
                            </div>
                            <MoodTracker onMoodSelect={handleMoodSelect} selectedMood={todayMood?.mood} />
                        </div>
                    </div>

                    {/* AI Devotional */}
                    <div className="bento-large animate-fade-in stagger-4" data-testid="ai-devotional-section">
                        <div className="verse-card h-full">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-primary" />
                                    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                                        AI-Powered Devotional
                                    </span>
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={generateAiDevotional}
                                    disabled={loadingAi}
                                    className="btn-ghost"
                                    data-testid="generate-devotional-btn"
                                >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingAi ? 'animate-spin' : ''}`} />
                                    {loadingAi ? 'Generating...' : 'Generate New'}
                                </Button>
                            </div>
                            {aiDevotional ? (
                                <p className="font-body text-lg text-[#2D2A26] leading-relaxed" data-testid="ai-devotional-text">
                                    {aiDevotional}
                                </p>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-muted-foreground mb-4">
                                        Generate a personalized devotional message for today
                                    </p>
                                    <Button 
                                        onClick={generateAiDevotional}
                                        disabled={loadingAi}
                                        className="btn-primary"
                                        data-testid="generate-first-devotional-btn"
                                    >
                                        {loadingAi ? 'Generating...' : 'Generate Devotional'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="bento-sidebar animate-fade-in stagger-5">
                        <div className="space-y-4">
                            <Link to="/journal" data-testid="journal-link">
                                <div className="journal-card flex items-center gap-4 hover:border-primary/30 cursor-pointer">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                                        <PenLine className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-[#2D2A26]">Write in Journal</h3>
                                        <p className="text-sm text-muted-foreground">Reflect on your day</p>
                                    </div>
                                </div>
                            </Link>

                            <Link to="/videos" data-testid="videos-link">
                                <div className="journal-card flex items-center gap-4 hover:border-primary/30 cursor-pointer">
                                    <div className="w-12 h-12 rounded-2xl bg-accent/20 flex items-center justify-center">
                                        <PlayCircle className="w-6 h-6 text-accent" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-[#2D2A26]">Inspirational Videos</h3>
                                        <p className="text-sm text-muted-foreground">Peaceful & uplifting</p>
                                    </div>
                                </div>
                            </Link>

                            <Link to="/moods" data-testid="moods-history-link">
                                <div className="journal-card flex items-center gap-4 hover:border-primary/30 cursor-pointer">
                                    <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center">
                                        <Calendar className="w-6 h-6 text-secondary-foreground" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-[#2D2A26]">Mood History</h3>
                                        <p className="text-sm text-muted-foreground">Track your journey</p>
                                    </div>
                                </div>
                            </Link>

                            <Link to="/prayers" data-testid="prayer-wall-link">
                                <div className="journal-card flex items-center gap-4 hover:border-primary/30 cursor-pointer">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                                        <Users className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-[#2D2A26]">Prayer Wall</h3>
                                        <p className="text-sm text-muted-foreground">Lift others in prayer</p>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
